import React, { useState, useEffect } from 'react';
import { CalendarEvent, EventType, EVENT_TEMPLATES, EventTemplate, EventTemplateFields } from '../types/calendar';
import { useAuth } from '../contexts/AuthContext';
import { X, Save } from 'lucide-react';
import './EventForm.css';

interface EventFormProps {
    event?: CalendarEvent | null;
    initialDateRange?: { start: Date; end: Date } | null;
    onSave: (eventData: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
}

export const EventForm: React.FC<EventFormProps> = ({ event, initialDateRange, onSave, onCancel }) => {
    const { user, isBypassMode } = useAuth();
    const [selectedEventType, setSelectedEventType] = useState<EventType>('CUSTOMER KEY DATE');
    const [selectedTemplate, setSelectedTemplate] = useState<EventTemplate>(EVENT_TEMPLATES[3]); // OTHER template
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        start: '',
        end: '',
        templateFields: {} as EventTemplateFields,
    });

    // Check if this is a synced event (read-only)
    const isSyncedEvent = event?.isSynced || false;

    useEffect(() => {
        if (event) {
            const eventType = event.eventType || 'OTHER';
            const template = EVENT_TEMPLATES.find(t => t.id === eventType) || EVENT_TEMPLATES[3];

            setSelectedEventType(eventType);
            setSelectedTemplate(template);
            setFormData({
                title: event.title,
                description: event.description,
                start: formatDateForInput(event.start),
                end: formatDateForInput(event.end),
                templateFields: event.templateFields || {},
            });
        }
    }, [event]);

    // Handle event type change
    useEffect(() => {
        const template = EVENT_TEMPLATES.find(t => t.id === selectedEventType) || EVENT_TEMPLATES[3];
        setSelectedTemplate(template);

        // Reset template fields when event type changes
        setFormData(prev => ({
            ...prev,
            templateFields: {},
        }));
    }, [selectedEventType]);

    // Handle initial date range from calendar selection
    useEffect(() => {
        if (initialDateRange && !event) {
            setFormData(prev => ({
                ...prev,
                start: formatDateForInput(initialDateRange.start),
                end: formatDateForInput(initialDateRange.end),
            }));
        }
    }, [initialDateRange, event]);

    const formatDateForInput = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            alert('Please enter a title for the event.');
            return;
        }

        if (!formData.start || !formData.end) {
            alert('Please select start and end dates for the event.');
            return;
        }

        const startDate = new Date(formData.start + 'T00:00:00');
        const endDate = new Date(formData.end + 'T23:59:59');

        if (endDate < startDate) {
            alert('End date must be on or after start date.');
            return;
        }

        const eventData: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'> = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            start: startDate,
            end: endDate,
            allDay: true,
            createdBy: isBypassMode ? 'Guest User' : (user?.username || user?.name || 'Unknown User'),
            eventType: selectedEventType,
            templateFields: Object.keys(formData.templateFields).length > 0 ? formData.templateFields : undefined,
        };

        onSave(eventData);
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onCancel();
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleTemplateFieldChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            templateFields: {
                ...prev.templateFields,
                [name]: value,
            },
        }));
    };

    const handleEventTypeChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setSelectedEventType(e.target.value as EventType);
    };

    return (
        <div className="modal-overlay" onClick={handleBackdropClick}>
            <div className="modal-content form-modal">
                <div className="modal-header">
                    <h2>{event ? 'Edit Event' : 'Create New Event'}</h2>
                    <button className="modal-close-btn" onClick={onCancel}>
                        <X size={20} />
                    </button>
                </div>

                {isSyncedEvent && (
                    <div className="sync-warning-form">
                        <div className="sync-warning-content">
                            <span>⚠️ This event is synced from {event?.syncSource} and cannot be edited.</span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="event-form">
                    <div className="form-group">
                        <label htmlFor="eventType">Event Type *</label>
                        <select
                            id="eventType"
                            name="eventType"
                            value={selectedEventType}
                            onChange={handleEventTypeChange}
                            required
                            disabled={isSyncedEvent}
                        >
                            {EVENT_TEMPLATES.filter(template => template.id !== 'PRODUCT RELEASE').map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                        <small className="help-text">{selectedTemplate.description}</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="title">Title *</label>
                        <input
                            id="title"
                            name="title"
                            type="text"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter event title"
                            disabled={isSyncedEvent}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder="Enter event description"
                            disabled={isSyncedEvent}
                        />
                    </div>

                    {/* Template-specific fields */}
                    {selectedTemplate.fields.map(field => (
                        <div key={field.name} className="form-group">
                            <label htmlFor={field.name}>
                                {field.label} {field.required && '*'}
                            </label>
                            {field.type === 'select' ? (
                                <select
                                    id={field.name}
                                    name={field.name}
                                    value={formData.templateFields[field.templateFieldKey as keyof EventTemplateFields] || ''}
                                    onChange={handleTemplateFieldChange}
                                    required={field.required}
                                    disabled={isSyncedEvent}
                                >
                                    <option value="">Select {field.label}</option>
                                    {field.options?.map(option => (
                                        <option key={option} value={option}>
                                            {option.replace('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            ) : field.type === 'textarea' ? (
                                <textarea
                                    id={field.name}
                                    name={field.name}
                                    value={formData.templateFields[field.templateFieldKey as keyof EventTemplateFields] || ''}
                                    onChange={handleTemplateFieldChange}
                                    required={field.required}
                                    placeholder={field.placeholder}
                                    rows={3}
                                    disabled={isSyncedEvent}
                                />
                            ) : (
                                <input
                                    id={field.name}
                                    name={field.name}
                                    type={field.type}
                                    value={formData.templateFields[field.templateFieldKey as keyof EventTemplateFields] || ''}
                                    onChange={handleTemplateFieldChange}
                                    required={field.required}
                                    placeholder={field.placeholder}
                                    disabled={isSyncedEvent}
                                />
                            )}
                        </div>
                    ))}

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="start">Start Date *</label>
                            <input
                                id="start"
                                name="start"
                                type="date"
                                value={formData.start}
                                onChange={handleInputChange}
                                required
                                disabled={isSyncedEvent}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="end">End Date *</label>
                            <input
                                id="end"
                                name="end"
                                type="date"
                                value={formData.end}
                                onChange={handleInputChange}
                                required
                                disabled={isSyncedEvent}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSyncedEvent}>
                            <Save size={16} />
                            {event ? 'Update Event' : 'Create Event'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
